import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { assignmentAPI } from '../services/api';

const AssignmentSubmissions = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [assignment, setAssignment] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [markingComplete, setMarkingComplete] = useState(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [assignmentRes, submissionsRes] = await Promise.all([
        assignmentAPI.getAssignmentById(id),
        assignmentAPI.getSubmissions(id)
      ]);

      setAssignment(assignmentRes.data.assignment);
      setSubmissions(submissionsRes.data.submissions);
    } catch (err) {
      setError(err.response?.data?.message || 'Error loading submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (submission) => {
    setSelectedSubmission(submission);
    setShowDetailModal(true);
  };

  const handleDeleteSubmission = async (submissionId, studentName) => {
    if (!window.confirm(`Are you sure you want to delete the submission from ${studentName}? This action cannot be undone.`)) {
      return;
    }

    try {
      await assignmentAPI.deleteSubmission(submissionId);
      // Refresh submissions list
      await fetchData();
      // Close modal if it's open
      if (showDetailModal && selectedSubmission?._id === submissionId) {
        setShowDetailModal(false);
        setSelectedSubmission(null);
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Error deleting submission');
    }
  };

  const handleMarkComplete = async (submissionId, currentStatus, studentName) => {
    const action = currentStatus ? 'incomplete' : 'complete';
    if (!window.confirm(`Mark ${studentName}'s submission as ${action}?`)) {
      return;
    }

    try {
      setMarkingComplete(submissionId);
      const response = await assignmentAPI.markSubmissionComplete(submissionId, !currentStatus);
      
      // Update submissions list
      setSubmissions(prevSubmissions => 
        prevSubmissions.map(sub => 
          sub._id === submissionId ? response.data.submission : sub
        )
      );

      // Update selected submission if modal is open
      if (selectedSubmission?._id === submissionId) {
        setSelectedSubmission(response.data.submission);
      }

      alert(response.data.message || `Submission marked as ${action}`);
    } catch (err) {
      alert(err.response?.data?.message || `Error marking submission as ${action}`);
    } finally {
      setMarkingComplete(null);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ fontSize: '20px', color: '#667eea' }}>Loading submissions... ✨</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ maxWidth: '600px', margin: '100px auto', padding: '30px', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
        <div style={{ fontSize: '20px', color: '#e53e3e', marginBottom: '20px' }}>{error}</div>
        <button 
          onClick={() => navigate('/admin/coding-assignments')}
          style={{
            padding: '12px 24px',
            backgroundColor: '#667eea',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >
          ← Back to Assignments
        </button>
      </div>
    );
  }

  if (!assignment) return null;

  const completedSubmissions = submissions.filter(s => s.isCompleted).length;
  const inProgressSubmissions = submissions.length - completedSubmissions;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f7fafc', padding: '20px' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          backgroundColor: 'white',
          padding: '20px 30px',
          borderRadius: '10px',
          marginBottom: '20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <button
            onClick={() => navigate('/admin/coding-assignments')}
            style={{
              padding: '8px 16px',
              backgroundColor: '#e2e8f0',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              color: '#2d3748',
              marginBottom: '15px'
            }}
          >
            ← Back to Assignments
          </button>

          <h1 style={{ color: '#2d3748', fontSize: '24px', marginBottom: '10px' }}>
            📊 Submissions for: {assignment.title}
          </h1>
          <div style={{ color: '#718096', fontSize: '14px', marginBottom: '15px' }}>
            Assignment Code: <span style={{ 
              fontFamily: 'monospace', 
              fontWeight: 'bold',
              backgroundColor: '#f7fafc',
              padding: '2px 8px',
              borderRadius: '4px'
            }}>{assignment.assignmentCode}</span>
          </div>

          {/* Stats */}
          <div style={{ display: 'flex', gap: '15px', marginTop: '15px' }}>
            <div style={{
              flex: 1,
              padding: '15px',
              backgroundColor: '#f7fafc',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#667eea' }}>
                {submissions.length}
              </div>
              <div style={{ fontSize: '14px', color: '#718096' }}>Total Submissions</div>
            </div>
            <div style={{
              flex: 1,
              padding: '15px',
              backgroundColor: '#f0fdf4',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#48bb78' }}>
                {completedSubmissions}
              </div>
              <div style={{ fontSize: '14px', color: '#718096' }}>Completed</div>
            </div>
            <div style={{
              flex: 1,
              padding: '15px',
              backgroundColor: '#fef2f2',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#fbbf24' }}>
                {inProgressSubmissions}
              </div>
              <div style={{ fontSize: '14px', color: '#718096' }}>In Progress</div>
            </div>
          </div>
        </div>

        {/* Submissions List */}
        {submissions.length === 0 ? (
          <div style={{
            backgroundColor: 'white',
            padding: '60px 30px',
            borderRadius: '10px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>📭</div>
            <h2 style={{ color: '#2d3748', marginBottom: '10px' }}>No Submissions Yet</h2>
            <p style={{ color: '#718096' }}>
              Students haven't started this assignment yet.
            </p>
          </div>
        ) : (
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '10px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ color: '#2d3748', marginBottom: '20px', fontSize: '18px' }}>
              Student Submissions ({submissions.length})
            </h2>

            <div style={{ display: 'grid', gap: '15px' }}>
              {submissions.map((submission) => {
                // Get total steps from algorithmByLanguage or fallback to steps array
                let totalSteps = 0;
                if (assignment.algorithmByLanguage && Object.keys(assignment.algorithmByLanguage).length > 0) {
                  const firstLang = Object.keys(assignment.algorithmByLanguage)[0];
                  totalSteps = assignment.algorithmByLanguage[firstLang]?.steps?.length || 0;
                } else {
                  totalSteps = assignment.steps?.length || 0;
                }
                
                const completedSteps = submission.stepSubmissions.filter(ss => ss.isComplete).length;
                // Calculate progress with 80% for steps, 20% for quizzes
                const stepsProgress = totalSteps > 0 ? (completedSteps / totalSteps) * 80 : 0;
                const quizProgress = submission.isCompleted ? 20 : 0;
                const progressPercent = Math.round(stepsProgress + quizProgress);

                return (
                  <div
                    key={submission._id}
                    style={{
                      padding: '20px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '10px',
                      transition: 'all 0.2s',
                      ':hover': { borderColor: '#667eea' }
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '15px' }}>
                      <div style={{ flex: 1 }}>
                        <h3 style={{ color: '#2d3748', marginBottom: '8px', fontSize: '16px' }}>
                          👤 {submission.user?.name || submission.user?.email || 'Unknown User'}
                        </h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                          <span style={{ color: '#718096', fontSize: '14px' }}>
                            📧 {submission.user?.email}
                          </span>
                          <span style={{ color: '#718096', fontSize: '14px' }}>
                            🕐 Last Updated: {new Date(submission.updatedAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <div style={{
                        padding: '8px 16px',
                        backgroundColor: submission.isCompleted ? '#48bb78' : '#fbbf24',
                        color: 'white',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}>
                        {submission.isCompleted ? '✓ Completed' : 'In Progress'}
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div style={{ marginBottom: '15px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                        <span style={{ fontSize: '14px', color: '#718096' }}>
                          Progress: {completedSteps}/{totalSteps} steps
                          {completedSteps === totalSteps && !submission.isCompleted && (
                            <span style={{ marginLeft: '6px', color: '#f59e0b', fontStyle: 'italic' }}>
                              • Quizzes pending
                            </span>
                          )}
                        </span>
                        <span style={{ fontSize: '14px', fontWeight: 'bold', color: progressPercent === 100 ? '#48bb78' : '#2d3748' }}>
                          {progressPercent}%
                        </span>
                      </div>
                      <div style={{
                        width: '100%',
                        height: '8px',
                        backgroundColor: '#e2e8f0',
                        borderRadius: '4px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${progressPercent}%`,
                          height: '100%',
                          backgroundColor: progressPercent === 100 ? '#48bb78' : '#667eea',
                          transition: 'width 0.3s'
                        }} />
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleViewDetails(submission)}
                        style={{
                          flex: 1,
                          padding: '12px',
                          backgroundColor: '#667eea',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          cursor: 'pointer'
                        }}
                      >
                        📄 View Details
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkComplete(
                            submission._id,
                            submission.isCompleted,
                            submission.user?.name || submission.user?.email || 'this student'
                          );
                        }}
                        disabled={markingComplete === submission._id}
                        style={{
                          padding: '12px 16px',
                          backgroundColor: submission.isCompleted ? '#f59e0b' : '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          cursor: markingComplete === submission._id ? 'not-allowed' : 'pointer',
                          opacity: markingComplete === submission._id ? 0.6 : 1
                        }}
                        title={submission.isCompleted ? 'Mark as Incomplete' : 'Mark as Complete'}
                      >
                        {markingComplete === submission._id ? '⏳' : (submission.isCompleted ? '↩️' : '✓')}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSubmission(
                            submission._id, 
                            submission.user?.name || submission.user?.email || 'this student'
                          );
                        }}
                        style={{
                          padding: '12px 16px',
                          backgroundColor: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          cursor: 'pointer'
                        }}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedSubmission && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '20px',
          overflow: 'auto'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            maxWidth: '900px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            padding: '30px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ color: '#2d3748', fontSize: '20px', margin: 0 }}>
                📄 Submission Details
              </h2>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => handleMarkComplete(
                    selectedSubmission._id,
                    selectedSubmission.isCompleted,
                    selectedSubmission.user?.name || selectedSubmission.user?.email || 'this student'
                  )}
                  disabled={markingComplete === selectedSubmission._id}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: selectedSubmission.isCompleted ? '#f59e0b' : '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: markingComplete === selectedSubmission._id ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    opacity: markingComplete === selectedSubmission._id ? 0.6 : 1
                  }}
                >
                  {markingComplete === selectedSubmission._id 
                    ? '⏳ Processing...' 
                    : (selectedSubmission.isCompleted ? '↩️ Mark Incomplete' : '✓ Mark Complete')
                  }
                </button>
                <button
                  onClick={() => setShowDetailModal(false)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#e2e8f0',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  ✕ Close
                </button>
              </div>
            </div>

            <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f7fafc', borderRadius: '8px' }}>
              <div style={{ marginBottom: '8px' }}>
                <strong>Student:</strong> {selectedSubmission.user?.name || selectedSubmission.user?.email}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Email:</strong> {selectedSubmission.user?.email}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Status:</strong>{' '}
                <span style={{
                  padding: '4px 8px',
                  backgroundColor: selectedSubmission.isCompleted ? '#48bb78' : '#fbbf24',
                  color: 'white',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}>
                  {selectedSubmission.isCompleted ? 'Completed' : 'In Progress'}
                </span>
              </div>
              <div>
                <strong>Last Updated:</strong> {new Date(selectedSubmission.updatedAt).toLocaleString()}
              </div>
            </div>

            <h3 style={{ color: '#2d3748', marginBottom: '15px', fontSize: '16px' }}>
              Step Submissions ({selectedSubmission.stepSubmissions.length})
            </h3>

            {selectedSubmission.stepSubmissions.map((stepSub, idx) => (
              <div
                key={idx}
                style={{
                  padding: '15px',
                  marginBottom: '15px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  backgroundColor: stepSub.isComplete ? '#f0fdf4' : '#fef2f2'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <h4 style={{ color: '#2d3748', margin: 0, fontSize: '15px' }}>
                    Step {stepSub.stepNumber}
                  </h4>
                  <span style={{
                    padding: '4px 8px',
                    backgroundColor: stepSub.isComplete ? '#48bb78' : '#ef4444',
                    color: 'white',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}>
                    {stepSub.isComplete ? '✓ Complete' : '✗ Incomplete'}
                  </span>
                </div>

                <div style={{ marginBottom: '10px' }}>
                  <strong style={{ fontSize: '13px', color: '#718096' }}>Language:</strong>{' '}
                  <span style={{
                    fontFamily: 'monospace',
                    backgroundColor: '#f7fafc',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}>
                    {stepSub.language}
                  </span>
                </div>

                <div style={{ marginBottom: '10px' }}>
                  <strong style={{ fontSize: '13px', color: '#718096' }}>Attempts:</strong> {stepSub.attemptNumber || 1}
                </div>

                {stepSub.aiReview?.feedback && (
                  <div style={{ marginBottom: '10px' }}>
                    <strong style={{ fontSize: '13px', color: '#718096' }}>AI Feedback:</strong>
                    <div style={{
                      marginTop: '5px',
                      padding: '10px',
                      backgroundColor: 'white',
                      borderRadius: '6px',
                      fontSize: '14px',
                      color: '#2d3748',
                      border: '1px solid #e2e8f0'
                    }}>
                      {stepSub.aiReview.feedback}
                    </div>
                  </div>
                )}

                <details style={{ marginTop: '10px' }}>
                  <summary style={{
                    cursor: 'pointer',
                    color: '#667eea',
                    fontWeight: 'bold',
                    fontSize: '13px',
                    padding: '5px 0'
                  }}>
                    📝 View Code
                  </summary>
                  <pre style={{
                    marginTop: '10px',
                    padding: '15px',
                    backgroundColor: '#1a202c',
                    color: '#f7fafc',
                    borderRadius: '6px',
                    overflow: 'auto',
                    fontSize: '13px',
                    fontFamily: 'monospace'
                  }}>
                    {stepSub.code}
                  </pre>
                </details>

                {stepSub.hintInteractions && stepSub.hintInteractions.length > 0 && (
                  <div style={{ marginTop: '10px' }}>
                    <strong style={{ fontSize: '13px', color: '#718096' }}>
                      Hints Used: {stepSub.hintInteractions.length}
                    </strong>
                  </div>
                )}
              </div>
            ))}

            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
              <button
                onClick={() => setShowDetailModal(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
              <button
                onClick={() => {
                  handleDeleteSubmission(
                    selectedSubmission._id,
                    selectedSubmission.user?.name || selectedSubmission.user?.email || 'this student'
                  );
                }}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                🗑️ Delete Submission
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignmentSubmissions;
